import { useUserIdentityContext } from "@core/services/user-identity.context";
import TagColor from "@core/ui/tag-color/tag-color";
import {
  GetReferralCodeModel,
  GetReferralModel,
  GetResponseReferralCodeModel,
  MutateResponseReferralModel,
} from "@factory/model/query-model-referral";
import {
  useQyCreateReferralCode,
  useQyGetReferral,
  useQyGetReferralCode,
} from "@factory/query/referral.query";
import ErrorSection from "@shared/ui/error-section/error-section";
import SkeletonList from "@shared/ui/skeleton-list/skeleton-list";
import TextBlock from "@shared/ui/text-block/text-block";
import useScreenSize from "@shared/utility/screen-size";
import { Button } from "primereact/button";
import { DataView } from "primereact/dataview";
import { Dialog } from "primereact/dialog";
import { PaginatorPageChangeEvent } from "primereact/paginator";
import { useState } from "react";
import QRCode from "react-qr-code";

export function CustomerListReferrals() {
  // THIS IS THE LOCAL DECLARATION
  const { getAuthUser } = useUserIdentityContext();
  const { userCode, role } = getAuthUser();
  const [rowLimit, setRowLimit] = useState(20);
  const [pageNumber, setPageNumber] = useState(0);
  const [searchTerm] = useState("");
  const [first, setFirst] = useState(0);
  const [sort] = useState({});
  const [layout] = useState<
    "grid" | "list" | (string & Record<string, unknown>)
  >("list");
  const [referralCodeData, setReferralCodeData] = useState<
    GetReferralCodeModel | undefined
  >();
  const [referralDialog, setReferralDialog] = useState(false);

  const { isMobileMode } = useScreenSize();

  // THIS IS THE QUERY DECLARATION
  const {
    data: referralResponse,
    isLoading,
    isError,
    error,
  } = useQyGetReferral(
    searchTerm,
    rowLimit,
    pageNumber,
    sort,
    {
      referrer: userCode,
    },
    true,
  );

  const onPageChange = (event: PaginatorPageChangeEvent) => {
    const offsetValue = event.page * rowLimit;
    setFirst(event.first);
    setPageNumber(offsetValue);
    setRowLimit(event.rows);
  };

  const handleGetRefCodeSuccess = (response: GetResponseReferralCodeModel) => {
    if (response) {
      setReferralCodeData(response as GetReferralCodeModel);
    }
  };
  useQyGetReferralCode(
    "",
    1,
    0,
    undefined,
    undefined,
    true,
    handleGetRefCodeSuccess,
  );

  const handleGenRefCodeSuccess = (response: MutateResponseReferralModel) => {
    if (!response.data) {
      return;
    }
    const referralCode = response.data as GetReferralCodeModel;
    setReferralCodeData(referralCode);
  };
  const { mutate: generateRefCode } = useQyCreateReferralCode(
    handleGenRefCodeSuccess,
  );

  const handleGenerateRefCode = () => {
    generateRefCode({ user_code: userCode });
  };

  // THIS IS THE LOCAL RENDERS
  const displayLoading = (
    <div className="card">
      <SkeletonList count={4} />
    </div>
  );
  const displayError = (
    <div className="card">
      <ErrorSection title="Error Occured" message={(error as any)?.message} />
    </div>
  );
  const gridItem = (referral: GetReferralModel) => {
    return (
      <div
        className="flex justify-between shadow-md rounded-md overflow-hidden border p-5"
        key={referral.code}
      >
        <div className="flex flex-col">
          <TextBlock
            label="Name"
            value={`${referral.referee_first_name} ${referral.referee_last_name}`}
          />
          <TextBlock
            label="Points Earned"
            value={`${referral.referral_points}`}
          />
          <TextBlock
            label="Stamps Earned"
            value={`${referral.referral_stamp}`}
          />
        </div>
        <div>
          <div className="flex justify-end">
            <TagColor value={referral.status || "PENDING"} />
          </div>
        </div>
      </div>
    );
  };
  const listItem = (referral: GetReferralModel, index: number) => {
    return (
      <div
        className="flex justify-between shadow-md rounded-md overflow-hidden border p-5"
        key={referral.code}
      >
        <div className="flex flex-col">
          <TextBlock
            label="Name"
            value={`${referral.referee_first_name} ${referral.referee_last_name}`}
          />
          <TextBlock
            label="Points Earned"
            value={`${referral.referral_points}`}
          />
          <TextBlock
            label="Stamps Earned"
            value={`${referral.referral_stamp}`}
          />
        </div>
        <div>
          <div className="flex justify-end">
            <TagColor value={referral.status || "PENDING"} />
          </div>
        </div>
      </div>
    );
  };

  const itemTemplate = (
    referral: GetReferralModel,
    layout: "grid" | "list",
    index: number,
  ) => {
    if (!referral) {
      return;
    }
    if (layout === "list") return listItem(referral, index);
    else if (layout === "grid") return gridItem(referral);
  };
  const listTemplate = (
    referrals: GetReferralModel[],
    layout: "grid" | "list",
  ): any => {
    return (
      <div
        className={`grid ${layout === "grid" ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"
          } gap-4 mt-4`}
      >
        {referrals.map((referral, index) =>
          itemTemplate(referral, layout, index),
        )}
      </div>
    );
  };

  const grid = (
    <section>
      <h5 className="mb-2">
        Total: <b>{referralResponse?.count}</b>
      </h5>
      <DataView
        value={referralResponse?.data}
        listTemplate={listTemplate}
        layout={layout}
        paginator
        rows={rowLimit}
        first={first}
        totalRecords={referralResponse?.count}
        rowsPerPageOptions={[10, 20, 30]}
        onPage={onPageChange}
      />
    </section>
  );

  const referralElement = (
    <Dialog
      // footer={footerContent}
      header="Referral Code"
      visible={referralDialog}
      style={{ width: "50vw" }}
      onHide={() => {
        if (!referralDialog) return;

        setReferralDialog(false);
      }}
      maximized={isMobileMode}
    >
      <div className="flex justify-center bg-white md:min-h-60">
        {referralCodeData?.referral_code ? (
          <section>
            <QRCode
              value={`${window.location.origin}/public/${role.slug}?referral=${referralCodeData?.referral_code}`}
              size={200}
            />
            <p className="text-xs font-semibold text-center mt-2">
              {referralCodeData?.referral_code}
            </p>
          </section>
        ) : (
          <div>
            <Button
              className="p-2 align-center"
              severity="info"
              label="Generate Referral Code"
              onClick={handleGenerateRefCode}
            />
          </div>
        )}
      </div>
    </Dialog>
  );

  return (
    <div id="ListActivities" className="campaign">
      {referralElement}
      <div className="p-4 bg-white">
        <div className="flex justify-end">
          <Button
            className="p-2 align-center"
            severity="info"
            label="Show Referral Information"
            onClick={() => setReferralDialog(true)}
          />
        </div>
        {isLoading && displayLoading}
        {isError && !isLoading && displayError}
        {!isLoading && !isError && grid}
      </div>
    </div>
  );
}

export default CustomerListReferrals;
